<template lang="pug">
	.web-service-caller.flex-col.space-large.full-width
		StructureCriteria(ref="criteria", :service="service", v-model="criteria", @change="(crt, pms, caps) => { params = pms; criteria = crt; paramsCaptions = caps; }", :valueResolver="getParamValue", :fullStructure="true")
			template(v-slot:parameters="{ data }")
				.flex-col.space-large.align-start
					.flex-row.space.justify
						DxButton(:text="hasRun ? $tCaption('Run again') : $tCaption('Run')", icon="fas fa-play", @click="run(data.validate)", :disabled="isRuning")
						DxButton(:text="$tCaption('Set as service defaults')", @click="setAsDefaults", :disabled="isRuning")
		.flex-row.space
			.flex-col.space.flex-1
				.flex-row.justify.space
					.font-big.upper.grey {{$tCaption('Results')}} {{isCached ? $tCaption('(cached)') : ''}}
					.flex-row
						DxDropDownButton(v-if="resultProcessingDropDownMenu.length", stylingMode="text", icon="more", :data-source="resultProcessingDropDownMenu", display-expr="text", key-expr="id", :use-select-mode="false", :showArrowIcon="false", 
							:drop-down-options="resultProcessing.dropDownOptions", @item-click="onDebugMenuClick")
				template(v-if="result")
					.data-view(v-if="runUrl")
						.font-small.upper.grey {{$tCaption('URL')}}
						DxTextArea(:value="runUrl", :height="100")
					.data-view
						.flex-row.space.align-center.justify
							.font-small.upper.grey {{$tCaption('Data')}}
							.flex-row.space
								.adt-button.rounded.flex-row.align-center.center.padded-lg.clickable.hoverable(@click="copyResultToClipboard")
									fas(icon="copy")
								.adt-button.rounded.flex-row.align-center.center.padded-lg.clickable.hoverable(@click="downloadResults")
									fas(icon="download")
						JsonViewer(v-if="result", :value="result")
						.flex-row.space.full-width.wrap(v-else)
							DxTextArea.flex-1(:value="resultData", :height="200", :width="520")
					.data-view(v-if="runPostData")
						.font-small.upper.grey {{$tCaption('Post data')}}
						span {{runPostData}}
					.data-view(v-if="showParsedValues && output && result")
						.font-small.upper.grey {{$tCaption('Parsed values')}}
						.flex-row.space.full-width.wrap
							DxTextArea(:value="resultOutput", :height="200", :width="520")
			.flex-col.flex-1(ref="mapRoot")
				.web-service-caller(ref="mapHolder")
					.flex-col.space(v-if="result && hasMap", :class="{ fullscreen }")
						LMap.result-processing-map(ref="map", :zoom="resultProcessing.map.zoom" :center="resultProcessing.map.center")
							LTileLayer(url="https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png", layer-type="base", name="OpenStreetMap")
							LControl(position="topleft")
								.small
									DxButton.small(icon="fas fa-maximize fa-2xs", @click="toggleFullscreen", :hint="$tCaption('Fullscreen')")
							LControl(position="topleft")
								.flex-col.space
									.exporter.flex-row.space.align-center(v-for="exporter in resultProcessing.map.exporters")
										DxButton.small(:icon="`fas fa-${exporter.icon} fa-2xs`", @click="processExporter(exporter)")
							LControl(position="bottomleft")
								.flex-col.legend(v-if="resultProcessing.map.legend")
									.legend-item.flex-row.space.align-center(v-for="legend in resultProcessing.map.legend")
										.icon(v-if="legend.icon", :style="{ '--icon': `url('${legend.icon}')`}")
										.color(v-if="legend.color", :style="{ '--color': legend.color, '--height': `${legend.weight || 2}px` }")
										.text {{legend.text}}
							LGeoJson(v-for="geoJson in defaultGeoJsons", :optionsStyle="geoJson.style", :geojson="geoJson")
							LFeatureGroup(ref="markersGroup")
								LMarker(v-for="marker in markersGeoJsons", :lat-lng="marker.geometry.coordinates")
									LTooltip(v-if="marker.tooltip")
										div(v-html="marker.tooltip")
									LPopup(v-if="marker.popupJson")
										.json-viewer-popup
											JsonViewer(:value="marker.popupJson")
									LPopup(v-else-if="marker.tooltip")
										div(v-html="marker.tooltip")
									LIcon(v-if="marker.iconUrl", :iconUrl="marker.iconUrl", :iconSize="marker.iconSize || [48,48]", :iconAnchor="marker.iconAnchor || [(marker.iconSize || 48) / 2, (marker.iconSize || 48) / 2]")
		Popup(v-if="resultProcessing.mapScriptPopupVisible", :popupVisible.sync="resultProcessing.mapScriptPopupVisible", :title="$tCaption('Map script')", :height="500", :width="640")
			.flex-col
				DxTextArea(:value.sync="resultProcessing.mapScript", :height="320", stylingMode="filled")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel')", @click="resultProcessing.mapScriptPopupVisible = false")
				DxButton(:text="$tCaption('Save')", type="default", @click="saveMapScript")
		Popup(v-if="resultProcessing.commonFunctionsPopupVisible", :popupVisible.sync="resultProcessing.commonFunctionsPopupVisible", :title="$tCaption('Common functions')", :height="500", :width="640")
			.flex-col
				DxTextArea(:value.sync="resultProcessing.commonFunctions", :height="320", stylingMode="filled")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel')", @click="resultProcessing.commonFunctionsPopupVisible = false")
				DxButton(:text="$tCaption('Save')", type="default", @click="saveCommonFunctions")
		Popup(v-if="resultProcessing.preProcessorScriptPopupVisible", :popupVisible.sync="resultProcessing.preProcessorScriptPopupVisible", :title="$tCaption('Results processing script')", :height="500", :width="640")
			.flex-col.space
				DxTextArea(:value.sync="resultProcessing.preProcessorScript", :height="320", stylingMode="filled")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Cancel')", @click="resultProcessing.preProcessorScriptPopupVisible = false")
				DxButton(:text="$tCaption('Save')", type="default", @click="savePreProcessorScript")
</template>

<script>
	import StructureCriteria from './StructureCriteria.vue';
	import DxButton from 'devextreme-vue/button';
	import DxTextArea from 'devextreme-vue/text-area';
	import Data from 'app-core/Data';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import DxSelectBox from 'devextreme-vue/select-box';
	import Popup from 'app-core/Popup.vue';
	import { LMap, LTileLayer, LMarker, LGeoJson, LFeatureGroup, LTooltip, LIcon, LPopup, LControl } from 'vue2-leaflet';
	import { featureGroup } from "leaflet";
	import JsonViewer from 'vue-json-viewer';
	import { saveAs } from 'file-saver';
	import circleToPolygon from 'circle-to-polygon';

	export default {
		translationCategory: 'webServiceCaller.Caller',
		name: 'web-service-caller',
		components: { 
			StructureCriteria, DxButton, DxTextArea, DxDropDownButton, Popup, LMap, LTileLayer, LMarker, LGeoJson, 
			LFeatureGroup, LTooltip, LIcon, JsonViewer, LPopup, LControl, circleToPolygon, DxSelectBox
		},
		props: ['service', 'startImmediate', 'showParsedValues'],
		data() {
			return {
				criteria: null,
				params: null,
				paramsCaptions: null,
				result: null,
				status: null,
				hasRun: false,
				output: null,
				lastQuery: null,
				isRuning: false,
				runUrl: null,
				runPostData: null,
				isCached: false,
				resultProcessing: {
					dropDownOptions: { width: '240px' },
					mapScriptPopupVisible: false,
					mapScript: null,
					map: {
						zoom: 8,
						geoJsons: [],
						center: [47.41322, -1.219482],
						legend: null
					},
					commonFunctions: null,
					commonFunctionsPopupVisible: false,
					preProcessorScript: null,
					preProcessorScriptPopupVisible: false,
					exporters: []
				},
				preProcessorResponse: null,
				originalResponse: null,
				fullscreen: false
			};
		},
		computed: {
			resultData() {
				return this.result && JSON.stringify(this.result, null, '\t');
			},
			resultOutput() {
				return this.output && JSON.stringify(this.output, null, '\t');
			},
			resultProcessingDropDownMenu() {
				return [
					{ id: 'commonFunctions', text: this.$tCaption('Common functions'), icon: 'fas fa-square-root-variable' },
					{ id: 'preProcessorScript', text: this.$tCaption('Results processing script'), icon: 'fas fa-chevron-right' },
					{ id: 'map', text: this.$tCaption('Map script'), icon: 'fas fa-location-dot' },
				];
			},
			hasMap() {
				return !!this.service?.JsonDefinition?.MapScript;
			},
			markersGeoJsons() {
				return this.resultProcessing.map.geoJsons.filter(f => `${f.geometry?.type}`.toLowerCase() === 'point');
			},
			defaultGeoJsons() {
				return this.resultProcessing.map.geoJsons.filter(f => !['point'].includes(`${f.geometry?.type}`.toLowerCase()));
			}
		},
		mounted() {
			if (__DEBUG__)
				window.wbsCaller = this;
			this.params = this.$refs.criteria && this.$refs.criteria.paramValues ? { ...this.$refs.criteria.paramValues } : {};
		},
		methods: {
			async processExporter(exporter) {
				const result = await exporter.process(this.result);
				if (!result) return;
				if (exporter.saveAs) {
					saveAs(new Blob([result], { type: exporter.type || 'text' }), exporter.saveAs);
				} else {
					this.$notifyMessage(result, { fullScreen: true });
				}
			},
			toggleFullscreen() {
				this.fullscreen = !this.fullscreen;
				if (this.fullscreen) {
					document.body.append(this.$refs.mapHolder);
				} else {
					this.$refs.mapRoot.append(this.$refs.mapHolder);
				}
				this.$nextTick(() => this.$refs.map?.mapObject?._onResize());
			},
			copyResultToClipboard() {
				navigator.clipboard.writeText(this.resultData);
			},
			downloadResults() {
				saveAs(
					new Blob([`//PARAMS\r\n${JSON.stringify(this.params || {}, void 0, '    ')}\r\n\r\n//RESULTS\r\n${this.resultData}`], { type: "text/plain;charset=utf-8" }), 
					`results_${this.$moment().format('MMDDHHmmss')}.txt`
				);
			},
			setAsDefaults() {
				this.$confirm(void 0, this.$tCaption('Do you want to set current parameters as service defaults?')).then(() => {
					this.service.JsonDefinition.Parameters.map(f => {
						if (this.params[f.Name]) {
							f.Value = `${this.params[f.Name]}`;
							this.$set(f, 'ValueCaption', this.paramsCaptions[f.Name] || null);
						}
					});
				});
			},
			fitGeoJsonBounds() {
				setImmediate(() => {
					var group = new featureGroup();

					let hasLayers = false;
					this.$refs.map.mapObject.eachLayer(function(layer) {
						if (layer.feature != undefined) {
							group.addLayer(layer);
							hasLayers = true;
						}
					});

					if (hasLayers) {
						this.$refs.map.mapObject.fitBounds(group.getBounds(), { padding: [20, 20] });
						return;
					}

					this.$refs.map.mapObject.fitBounds(this.$refs.markersGroup.mapObject.getBounds());
				});
			},
			buildDebugInfo() {
				this.buildDebugMapGeoJsons();
			},
			buildDebugMapGeoJsons() {
				if (!this.service.JsonDefinition?.MapScript) {
					this.resultProcessing.map.geoJsons = [];
					return;
				}

				try {
					const evalText = `
						var func = ($, $params, $funcs) => {
							${this.service.JsonDefinition?.MapScript};
						};

						func(this.data, this.params, this.funcs);
					`;
					const results = (function() {
						if (__DEBUG__)
							console.debug('buildDebugMapGeoJsons', { evalText });

						return eval(evalText);
					}).apply({
						data: this.result,
						params: this.params,
						funcs: {
							circleToPolygon
						}
					});

					this.resultProcessing.map.geoJsons = Array.isArray(results) ? results : results?.geoJsons || [];
					this.resultProcessing.map.legend = results?.legend;
					this.resultProcessing.map.exporters = results?.exporters || [];
					this.fitGeoJsonBounds();
				} catch(ex) {
					console.error(ex);
				}
			},
			saveMapScript() {
				this.$set(this.service.JsonDefinition, 'MapScript', this.resultProcessing.mapScript);
				this.resultProcessing.mapScriptPopupVisible = false;
				this.buildDebugInfo();
			},
			saveCommonFunctions() {
				this.$set(this.service.JsonDefinition, 'CommonFunctions', this.resultProcessing.commonFunctions);
				this.resultProcessing.commonFunctionsPopupVisible = false;
				this.buildDebugInfo();
			},
			savePreProcessorScript() {
				this.$set(this.service.JsonDefinition, 'PreProcessorScript', this.resultProcessing.preProcessorScript);
				this.resultProcessing.preProcessorScriptPopupVisible = false;
				this.buildDebugInfo();
			},
			showMapDebugScript() {
				this.resultProcessing.mapScriptPopupVisible = false;
				this.resultProcessing.mapScript = this.service.JsonDefinition?.MapScript;
				setImmediate(() => this.resultProcessing.mapScriptPopupVisible = true);
			},
			showCommonFunctions() {
				this.resultProcessing.commonFunctionsPopupVisible = false;
				this.resultProcessing.commonFunctions = this.service.JsonDefinition?.CommonFunctions;
				setImmediate(() => this.resultProcessing.commonFunctionsPopupVisible = true);
			},
			showPreProcessorScript() {
				this.resultProcessing.preProcessorScriptPopupVisible = false;
				this.resultProcessing.preProcessorScript = this.service.JsonDefinition?.PreProcessorScript;
				setImmediate(() => this.resultProcessing.preProcessorScriptPopupVisible = true);
			},
			onDebugMenuClick(e) {
				switch(e.itemData.id) {
					case 'commonFunctions':
						this.showCommonFunctions();
						break;
					case 'preProcessorScript':
						this.showPreProcessorScript();
						break;
					case 'map':
						this.showMapDebugScript();
						break;
				}
			},
			async run(validate) {
				var isValid = !validate || await validate();
				if (__DEBUG__)
					console.debug('Run', { validate, isValid });

				if (!isValid) return;
				var params = Object.keys(this.params).filter(f => f && this.params[f]).map(f => ({
					Name: f,
					Value: this.params[f]
				}));
				this.isRuning = true;
				this.runUrl = null;
				this.runPostData = null;
				Data.postExternalApi('ExternalService/RunExternalService', {
					ExternalServiceOid: this.service.Oid,
					FormatResponse: !!this.service.JsonDefinition.Outputs.length,
					Parameters: params,
					CalculateOutputs: this.showParsedValues ? [] : ['no_outputs']
				}).then(r => {
					if (__DEBUG__)
						console.debug('RunExternalService', { r });
					if (r.Success) {
						const response = r?.Data?.PreProcessedResult || r?.Data?.OriginalResponse;
						this.result = response && JSON.parse(response) || {};
						this.preProcessorResponse = r?.Data?.PreProcessedResult;
						this.originalResponse = r?.Data?.OriginalResponse;

						if (r?.Data?.PreProcessorError) {
							this.$notifyError(r?.Data?.PreProcessorError);
						}

						if (this.service.JsonDefinition.Outputs.length)
							this.output = r.Data.FormattedResponse && JSON.parse(r.Data.FormattedResponse) || {};
						this.$emit('output', this.result, params, this.originalResponse);
						this.hasRun = true;
						this.runUrl = r?.Data?.Url;
						this.isCached = r?.Data?.IsCachedResponse;
						if (r?.Data?.PostData !== '{}') {
							this.runPostData = r?.Data?.PostData;
						}
						this.buildDebugInfo();
					} else {
						this.result = r.ErrorMessage;
						this.$notifyError(r.ErrorMessage);
					}
				}).finally(() => this.isRuning = false);
			},
			getReplacedQuery() {
				var query = this.service.Url || '';
				var params = this.params || {};
				this.service.JsonDefinition.Parameters.map(f => {
					query = query.replace(new RegExp(`{${f.Name}}`, 'g'), this.getParamValue(f, params[f.Name]));
				});
				return query;
			},
			getParams(paramType) {
				var res = {};
				this.service.JsonDefinition.Parameters.filter(f => f.UserEditable && (!paramType || f.ParamType === paramType)).map(p => {
					res[p.Name] = this.getParamValue(p, this.params[p.Name]);
				});
				return res;
			},
			getParamValue(param, value) {
				return this.$Data.externalServiceGetParamValue(param, value);
			},
			parseResults() {
				this.output = {};
				(this.service.JsonDefinition.Outputs || []).map(f => this.output[f.Name] = f.OutputEvalType === 0 ? this.calcScript(f.Path, this.result, f.Name) : this.getPropValue(f.Path, this.result, f.Name));
			},
			getPropValue(path, object, outputName) {
				path = `${path}`.split('.');

				var result = object;
				while (path.length && result) {
					if (Array.isArray(result)) {
						result = result[0];
					}
					result = result[path[0]];
					path.splice(0, 1);
				}

				return result;
			},
			calcScript(script, object, outputName) {
				try {
					var params = this.getParams();
					Object.keys(params).map(key => {
						script = script.replace(new RegExp(`@${key}`, 'g'), params[key]);
					});

					if (__DEBUG__)
						console.debug('Calc script', { script });

					var func = eval(`($) => { ${script.indexOf('return') === -1 ? 'return' : ''} ${script} }`);
					var result = func(object);

					if (__DEBUG__)
						console.debug('Calc script result', { func, result });

					return result;
				} catch(ex) {
					this.$notifyError(`${this.$tCaption('Error occurs while calculate output script for')} ${outputName}: ${ex}`);
				}
			}
		}
	};
</script>
<style lang='scss'>
	.web-service-caller {
		@import "main/themes/generated/variables.base.scss";

		padding: 20px;

		.data-view {
			background: #fff;
			padding: 20px;
			border-radius: 4px;
		}

		.result-processing-map {
			min-height: 300px;
			height: 50vh;
		}

		.fullscreen {
			.vue2leaflet-map {
				position: fixed !important;
				left: 0px;
				top: 0px;
				bottom: 0px;
				right: 0px;
				z-index: 10000000;
				height: 100%;
			}
		}

		.legend {
			background: rgba(255,255,255,0.5);
			padding: 6px;
			border-radius: 2px;
			pointer-events: none;
			user-select: none;

			.legend-item {
				font-size: 10px;
			}

			.color {
				width: 20px;
				height: var(--height);
				background: var(--color);
			}

			.icon {
				width: 14px;
				height: 14px;
				background: var(--icon);
				background-size: cover;
			}
		}

		.json-viewer-popup {
			overflow: auto;
			height: 240px;
		}
	}
</style>
